import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../auth/useAuth';
import AuthModal from '../components/AuthModal';
import {
  markSmartHealingLinkedUser,
  markSmartHealingOriginSession,
} from '../utils/smartHealingAccess';

export default function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const gatewayApiUrl = /^https?:\/\//i.test(apiUrl) ? apiUrl : '';
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const rawHandoffToken = String(params.get('sh_handoff') || '').trim();
  const queryEmail = String(params.get('email') || '').trim();

  const [open, setOpen] = useState(true);
  const [initialEmail, setInitialEmail] = useState(queryEmail);
  const [activeHandoffToken, setActiveHandoffToken] = useState(rawHandoffToken);
  const [infoMessage, setInfoMessage] = useState(
    rawHandoffToken
      ? '스마트힐링에서 연결되었습니다. 기존 AI닥터큐 회원이면 로그인 후 스마트힐링으로 다시 연결됩니다.'
      : 'AI닥터큐 서비스를 이용하려면 로그인해 주세요.',
  );

  useEffect(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    if (rawHandoffToken) {
      markSmartHealingOriginSession();
    }
  }, [rawHandoffToken]);

  useEffect(() => {
    let mounted = true;

    const loadHandoff = async () => {
      if (!rawHandoffToken) {
        return;
      }

      try {
        const { data } = await axios.get(`${gatewayApiUrl}/api/v1/smart-healing/handoff/${rawHandoffToken}`);
        if (!mounted) {
          return;
        }

        const nextEmail = String(data?.email || '').trim();
        if (nextEmail) {
          setInitialEmail(nextEmail);
        }

        setActiveHandoffToken(rawHandoffToken);
        setInfoMessage('스마트힐링 연결 정보가 확인되었습니다. 기존 AI닥터큐 회원은 로그인 후 바로 돌아갑니다.');
      } catch {
        if (!mounted) {
          return;
        }

        setActiveHandoffToken('');
        setInfoMessage('스마트힐링 연결 정보가 만료되어 일반 로그인으로 전환되었습니다.');
      }
    };

    void loadHandoff();

    return () => {
      mounted = false;
    };
  }, [gatewayApiUrl, rawHandoffToken]);

  const signupPath = activeHandoffToken
    ? `/signup/step1?sh_handoff=${encodeURIComponent(activeHandoffToken)}`
    : '/signup/step1';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AuthModal
        isOpen={open}
        defaultMode="login"
        initialEmail={initialEmail}
        loginPayload={activeHandoffToken ? { smart_healing_handoff_token: activeHandoffToken } : null}
        signupPath={signupPath}
        infoMessage={infoMessage}
        onClose={() => {
          setOpen(false);
          navigate('/', { replace: true });
        }}
        onSuccess={(data) => {
          if (activeHandoffToken) {
            markSmartHealingOriginSession();
          }

          if (activeHandoffToken && data?.user_no) {
            markSmartHealingLinkedUser(data.user_no);
          }

          auth.actions.loginSuccess(data, {
            source: 'web',
            preserveExistingIntent: true,
          });

          setOpen(false);

          if (data?.smart_healing_launch_url) {
            window.location.replace(data.smart_healing_launch_url);
            return false;
          }

          const intent = auth.actions.consumeIntent();
          if (intent?.kind === 'web' && intent.path) {
            navigate(intent.path, { replace: true });
            return false;
          }

          navigate('/', { replace: true });
          return false;
        }}
      />
    </div>
  );
}
